import { useEffect, useState } from "react";
import api from "../api/client";
import { useNavigate } from "react-router-dom";
import JobPrefill from "./JobPrefill";

const CURRENCIES = ["EUR", "SEK", "GBP", "USD", "NOK", "DKK", "CHF", "PLN"];

const GLOBAL_AREAS = [
  "EMEA",
  "APAC",
  "AMER",
  "LATAM",
  "DACH",
  "Nordics",
  "CEE",
  "MENA",
  "SEA",
  "ANZ",
  "Global",
];

const DEFAULT_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
];

const empty = {
  company: "",
  job_title: "",
  job_url: "",
  contact_person: "",
  salary_min: "",
  salary_max: "",
  salary_currency: "EUR",
  notes: "",
  location_country: "",
  location_region: "",
  location_city: "",
  location_global_area: "",
  is_remote: false,
  application_period_end: "",
  applied_at: new Date().toISOString().split("T")[0],
  initial_status: "Applied",
  cover_letter_status: null,
};

export default function ApplicationPanel({
  open,
  onClose,
  onSave,
  editingApp,
}) {
  const isEdit = !!editingApp;

  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      if (editingApp) {
        setForm({
          company: editingApp.company ?? "",
          job_title: editingApp.job_title ?? "",
          job_url: editingApp.job_url ?? "",
          contact_person: editingApp.contact_person ?? "",
          salary_min: editingApp.salary_min ?? "",
          salary_max: editingApp.salary_max ?? "",
          salary_currency: editingApp.salary_currency ?? "EUR",
          notes: editingApp.notes ?? "",
          location_country: editingApp.location_country ?? "",
          location_region: editingApp.location_region ?? "",
          location_city: editingApp.location_city ?? "",
          location_global_area: editingApp.location_global_area ?? "",
          is_remote: editingApp.is_remote ?? false,
          applied_at:
            editingApp.applied_at?.split("T")[0] ??
            new Date().toISOString().split("T")[0],
          application_period_end: editingApp.application_period_end ?? "",
          cover_letter_status: editingApp.cover_letter_status ?? null,
          initial_status: "Applied",
          cover_letter_status: editingApp.cover_letter_status ?? null,
        });
      } else {
        setForm(empty);
      }
      setError(null);
    }
  }, [editingApp, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? parseInt(form.salary_min) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max) : null,
        applied_at: form.applied_at
          ? new Date(form.applied_at).toISOString()
          : null,
        application_period_end: form.application_period_end || null,
        location_global_area: form.location_global_area || null,
        job_url: form.job_url || null,
        contact_person: form.contact_person || null,
        location_country: form.location_country || null,
        location_region: form.location_region || null,
        location_city: form.location_city || null,
      };
      if (isEdit) {
        await api.patch(`/applications/${editingApp.id}`, payload);
      } else {
        try {
          await api.post("/applications", payload);
        } catch (err) {
          if (err.response?.status === 403) {
            setSaving(false);
            setError(
              "You've reached the 20 application limit on the free plan. Upgrade to Pro for unlimited applications.",
            );
            return;
          }
          throw err;
        }
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white text-base">
            {isEdit ? "Edit application" : "Add application"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form
            onSubmit={handleSubmit}
            id="app-form"
            className="flex flex-col gap-5"
          >
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {error}
                </p>
                {true && (
                  <button
                    onClick={() => {
                      onClose();
                      navigate("/pricing");
                    }}
                    className="mt-2 text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                  >
                    View Pro plans →
                  </button>
                )}
              </div>
            )}

            {/* Company + Job title */}
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className={labelClass}>Company *</label>
                <input
                  name="company"
                  required
                  value={form.company}
                  onChange={handleChange}
                  placeholder="e.g. Spotify"
                  className={inputClass}
                />
                <JobPrefill
                  company={form.company}
                  onSelect={(job) =>
                    setForm({
                      ...form,
                      company: job.company ?? form.company,
                      job_title: job.job_title ?? form.job_title,
                      job_url: job.job_url ?? form.job_url,
                      location_city: job.location_city ?? form.location_city,
                      location_region: job.location_region ?? form.location_region,
                      location_country: job.location_country ?? form.location_country,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Job title *</label>
                <input
                  name="job_title"
                  required
                  value={form.job_title}
                  onChange={handleChange}
                  placeholder="e.g. Senior Frontend Developer"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Job URL */}
            <div>
              <label className={labelClass}>Job URL</label>
              <input
                name="job_url"
                type="url"
                value={form.job_url}
                onChange={handleChange}
                placeholder="https://..."
                className={inputClass}
              />
            </div>

            {/* Contact person */}
            <div>
              <label className={labelClass}>Contact person</label>
              <input
                name="contact_person"
                value={form.contact_person}
                onChange={handleChange}
                placeholder="e.g. Jane Smith (HR)"
                className={inputClass}
              />
            </div>

            {/* Application date */}
            <div>
              <label className={labelClass}>Application date</label>
              <input
                name="applied_at"
                type="date"
                value={form.applied_at}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Apply by — closing date of the posting */}
            <div>
              <label className={labelClass}>Apply by</label>
              <input
                name="application_period_end"
                type="date"
                value={form.application_period_end}
                onChange={handleChange}
                className={inputClass}
              />
              <p className="text-xs text-gray-400 mt-1">
                Often shown on the job posting. We use this to detect ghosting
                more accurately — leave blank if the posting doesn't say.
              </p>
            </div>

            {/* Salary */}
            <div>
              <label className={labelClass}>Salary expectation</label>
              <div className="flex gap-2">
                <input
                  name="salary_min"
                  type="number"
                  min="0"
                  value={form.salary_min}
                  onChange={handleChange}
                  placeholder="Min"
                  className={inputClass}
                />
                <input
                  name="salary_max"
                  type="number"
                  min="0"
                  value={form.salary_max}
                  onChange={handleChange}
                  placeholder="Max"
                  className={inputClass}
                />
                <select
                  name="salary_currency"
                  value={form.salary_currency}
                  onChange={handleChange}
                  className={`${inputClass} w-28`}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className={labelClass}>Location</label>
              <div className="flex flex-col gap-2">
                <input
                  name="location_country"
                  value={form.location_country}
                  onChange={handleChange}
                  placeholder="Country (e.g. Sweden)"
                  className={inputClass}
                />
                <input
                  name="location_region"
                  value={form.location_region}
                  onChange={handleChange}
                  placeholder="Region (e.g. Stockholm)"
                  className={inputClass}
                />
                <input
                  name="location_city"
                  value={form.location_city}
                  onChange={handleChange}
                  placeholder="City (optional)"
                  className={inputClass}
                />
                <select
                  name="location_global_area"
                  value={form.location_global_area}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Global area (optional)</option>
                  {GLOBAL_AREAS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none mt-1">
                  <input
                    name="is_remote"
                    type="checkbox"
                    checked={form.is_remote}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                  />
                  Remote position
                </label>
                <p className="text-xs text-gray-400 -mt-1">
                  Remote and location can both be set — e.g. remote but based in
                  EMEA.
                </p>
              </div>
            </div>

            {/* Initial status — new applications only */}
            {!isEdit && (
              <div>
                <label className={labelClass}>Initial status</label>
                <select
                  name="initial_status"
                  value={form.initial_status}
                  onChange={handleChange}
                  className={inputClass}
                >
                  {DEFAULT_STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Interview prep, impressions, follow-up reminders..."
                rows={4}
                className={`${inputClass} resize-none`}
              />
            </div>

            <div className="mt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.cover_letter_status === "submitted_self"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      cover_letter_status: e.target.checked
                        ? "submitted_self"
                        : null,
                    })
                  }
                  className="mt-0.5 cursor-pointer"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  I submitted a cover letter with this application
                  <span className="block text-xs text-gray-400 mt-0.5">
                    Helps Joborigo measure cover letter impact across
                    industries.
                  </span>
                </span>
              </label>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="app-form"
            disabled={saving}
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? "Saving..." : isEdit ? "Save changes" : "Add application"}
          </button>
        </div>
      </div>
    </>
  );
}
